import React, { useCallback, useEffect, useState } from 'react'
import SidebarNew from '../../components/sidebar/SidebarNew'
import { Modal } from 'antd'
import { ExclamationCircleFilled, SearchOutlined } from '@ant-design/icons'
import Swal from 'sweetalert2'
import {
    viewSubscriptions,
    subscriptionUsers,
    listPlans,
    addSubscription,
    updateSubscription,
    cancelSubscription,
} from '../../api/AdminRequest'

const formatDate = (d) => {
    if (!d) return '-'
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

const statusBadge = (status) => {
    const map = {
        active: 'bg-green-100 text-green-700',
        cancelled: 'bg-red-100 text-red-700',
        expired: 'bg-gray-200 text-gray-600',
    }
    return (
        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${map[status] || 'bg-blue-100 text-blue-700'}`}>
            {status || 'unknown'}
        </span>
    )
}

const emptyForm = {
    userID: '',
    PlanID: '',
    planStartDate: '',
    planExpiryDate: '',
    billingCycle: 'yearly',
    paymentStatus: 'success',
}

function ViewSubscriptionsPage() {
    const [subscriptions, setSubscriptions] = useState([])
    const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, total: 0 })
    const [page, setPage] = useState(1)
    const [search, setSearch] = useState('')
    const [searchInput, setSearchInput] = useState('')
    const [statusFilter, setStatusFilter] = useState('all')
    const [loading, setLoading] = useState(true)

    const [plans, setPlans] = useState([])
    const [modalOpen, setModalOpen] = useState(false)
    const [editing, setEditing] = useState(null)
    const [form, setForm] = useState(emptyForm)
    const [userOptions, setUserOptions] = useState([])
    const [userSearch, setUserSearch] = useState('')
    const [saving, setSaving] = useState(false)

    const fetchSubscriptions = useCallback(async () => {
        setLoading(true)
        try {
            const { data } = await viewSubscriptions(page, 10, search, statusFilter)
            if (data.success) {
                setSubscriptions(data.subscriptions)
                setPagination(data.pagination)
            }
        } catch (error) {
            console.log(error?.response?.data)
            Swal.fire('Error', 'Failed to load subscriptions', 'error')
        } finally {
            setLoading(false)
        }
    }, [page, search, statusFilter])

    useEffect(() => { fetchSubscriptions() }, [fetchSubscriptions])

    useEffect(() => {
        listPlans()
            .then(({ data }) => { if (data.success) setPlans(data.plans) })
            .catch((e) => console.log(e?.response?.data))
    }, [])

    const fetchUserOptions = useCallback(async (q) => {
        try {
            const { data } = await subscriptionUsers(q)
            if (data.success) setUserOptions(data.users)
        } catch (error) {
            console.log(error?.response?.data)
        }
    }, [])

    useEffect(() => {
        if (!modalOpen) return
        const t = setTimeout(() => fetchUserOptions(userSearch), 300)
        return () => clearTimeout(t)
    }, [modalOpen, userSearch, fetchUserOptions])

    const openAddModal = () => {
        setEditing(null)
        setForm(emptyForm)
        setUserSearch('')
        setUserOptions([])
        setModalOpen(true)
    }

    const openEditModal = (sub) => {
        setEditing(sub)
        setForm({
            userID: sub.userID?._id || '',
            PlanID: sub.planID || '',
            planStartDate: sub.planStartDate ? new Date(sub.planStartDate).toISOString().slice(0, 10) : '',
            planExpiryDate: sub.planExpiryDate ? new Date(sub.planExpiryDate).toISOString().slice(0, 10) : '',
            billingCycle: sub.billingCycle || 'yearly',
            paymentStatus: sub.paymentStatus || 'success',
        })
        setModalOpen(true)
    }

    const handleSave = async () => {
        if (!form.PlanID || !form.planStartDate || !form.planExpiryDate || (!editing && !form.userID)) {
            Swal.fire('Warning', 'Please fill user, plan and both dates', 'warning')
            return
        }
        if (new Date(form.planExpiryDate) <= new Date(form.planStartDate)) {
            Swal.fire('Warning', 'Expiry date must be after start date', 'warning')
            return
        }

        setSaving(true)
        try {
            if (editing) {
                const payload = {
                    PlanID: form.PlanID,
                    planStartDate: form.planStartDate,
                    planExpiryDate: form.planExpiryDate,
                    billingCycle: form.billingCycle,
                    paymentStatus: form.paymentStatus,
                }
                const { data } = await updateSubscription(editing._id, payload)
                if (data.success) {
                    Swal.fire({ icon: 'success', title: 'Subscription updated', timer: 1800, showConfirmButton: false })
                }
            } else {
                const { data } = await addSubscription(form)
                if (data.success) {
                    Swal.fire({ icon: 'success', title: 'Subscription added', timer: 1800, showConfirmButton: false })
                }
            }
            setModalOpen(false)
            fetchSubscriptions()
        } catch (error) {
            Swal.fire('Error', error?.response?.data?.message || 'Failed to save subscription', 'error')
        } finally {
            setSaving(false)
        }
    }

    const handleCancelSub = (sub) => {
        Modal.confirm({
            title: 'Cancel this subscription?',
            icon: <ExclamationCircleFilled />,
            content: `${sub.userName} will lose access when the plan is cancelled.`,
            okText: 'Yes, cancel it',
            okType: 'danger',
            cancelText: 'No',
            async onOk() {
                try {
                    const { data } = await cancelSubscription(sub._id)
                    if (data.success) {
                        Swal.fire({ icon: 'success', title: data.message, timer: 1800, showConfirmButton: false })
                        fetchSubscriptions()
                    }
                } catch (error) {
                    Swal.fire('Error', error?.response?.data?.message || 'Failed to cancel', 'error')
                }
            },
        })
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        setPage(1)
        setSearch(searchInput.trim())
    }

    const selectedUser = userOptions.find((u) => u._id === form.userID)

    return (
        <div className="flex flex-col">
            <div className="flex flex-col">
                <div className="flex">
                    <div className="h-full z-10 overflow-y-auto">
                        <SidebarNew />
                    </div>
                    <div className="lg:flex-1 overflow-auto scrollbar-hide h-screen w-full md:z-10 p-4 lg:p-10 md:p-10">
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                            <h1 className="text-2xl font-bold">Plan Subscriptions</h1>
                            <button
                                onClick={openAddModal}
                                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                + Add Subscription
                            </button>
                        </div>

                        {/* Filters */}
                        <div className="flex flex-wrap items-center gap-3 mb-4">
                            <form onSubmit={handleSearchSubmit} className="relative">
                                <input
                                    type="text"
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    placeholder="Search by user name or email"
                                    className="w-72 pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <SearchOutlined className="absolute left-3 top-2.5 text-gray-400" />
                            </form>
                            <select
                                value={statusFilter}
                                onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
                                className="py-2 px-3 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="all">All statuses</option>
                                <option value="active">Active</option>
                                <option value="cancelled">Cancelled</option>
                                <option value="expired">Expired</option>
                            </select>
                        </div>

                        {/* Table */}
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-gray-500 border-b border-gray-200">
                                        <th className="px-4 py-3 font-medium">User</th>
                                        <th className="px-4 py-3 font-medium">Plan</th>
                                        <th className="px-4 py-3 font-medium">Start</th>
                                        <th className="px-4 py-3 font-medium">Expiry</th>
                                        <th className="px-4 py-3 font-medium">Cycle</th>
                                        <th className="px-4 py-3 font-medium">Payment</th>
                                        <th className="px-4 py-3 font-medium">Status</th>
                                        <th className="px-4 py-3 font-medium text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400">Loading...</td></tr>
                                    ) : subscriptions.length === 0 ? (
                                        <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400">No subscriptions found</td></tr>
                                    ) : subscriptions.map((sub) => (
                                        <tr key={sub._id} className="border-b border-gray-100 hover:bg-gray-50">
                                            <td className="px-4 py-3">
                                                <p className="font-medium capitalize">{sub.userName || '-'}</p>
                                                <p className="text-xs text-gray-500">{sub.userEmail}</p>
                                            </td>
                                            <td className="px-4 py-3">{sub.planName || '-'}</td>
                                            <td className="px-4 py-3">{formatDate(sub.planStartDate)}</td>
                                            <td className="px-4 py-3">{formatDate(sub.planExpiryDate)}</td>
                                            <td className="px-4 py-3 capitalize">{sub.billingCycle || '-'}</td>
                                            <td className="px-4 py-3 capitalize">{sub.paymentStatus || '-'}</td>
                                            <td className="px-4 py-3">{statusBadge(sub.status)}</td>
                                            <td className="px-4 py-3">
                                                <div className="flex justify-end gap-2">
                                                    <button
                                                        onClick={() => openEditModal(sub)}
                                                        className="px-3 py-1.5 text-xs bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
                                                    >
                                                        Edit
                                                    </button>
                                                    {sub.status === 'active' && (
                                                        <button
                                                            onClick={() => handleCancelSub(sub)}
                                                            className="px-3 py-1.5 text-xs bg-red-50 text-red-600 border border-red-200 rounded-md hover:bg-red-100 transition-colors"
                                                        >
                                                            Cancel
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="flex items-center justify-between mt-4">
                            <p className="text-sm text-gray-500">
                                Total: {pagination.total} subscription{pagination.total === 1 ? '' : 's'}
                            </p>
                            <div className="flex items-center gap-2">
                                <button
                                    disabled={page <= 1}
                                    onClick={() => setPage(page - 1)}
                                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50"
                                >
                                    Prev
                                </button>
                                <span className="text-sm">Page {pagination.currentPage} / {pagination.totalPages || 1}</span>
                                <button
                                    disabled={!pagination.hasMore}
                                    onClick={() => setPage(page + 1)}
                                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50"
                                >
                                    Next
                                </button>
                            </div>
                        </div>

                        {/* Add / Edit modal */}
                        <Modal
                            title={editing ? 'Edit Subscription' : 'Add Subscription'}
                            open={modalOpen}
                            onOk={handleSave}
                            confirmLoading={saving}
                            onCancel={() => setModalOpen(false)}
                            okText={editing ? 'Update' : 'Create'}
                            width={560}
                        >
                            <div className="space-y-4 pt-2">
                                {!editing && (
                                    <div>
                                        <label className="block text-sm font-medium mb-1">User</label>
                                        <input
                                            type="text"
                                            value={userSearch}
                                            onChange={(e) => setUserSearch(e.target.value)}
                                            placeholder="Search standalone users by name or email"
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
                                        />
                                        <select
                                            value={form.userID}
                                            onChange={(e) => setForm({ ...form, userID: e.target.value })}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="">Select a user</option>
                                            {userOptions.map((u) => (
                                                <option key={u._id} value={u._id} disabled={u.hasActiveSubscription}>
                                                    {u.name} ({u.email}){u.hasActiveSubscription ? ' - already subscribed' : ''}
                                                </option>
                                            ))}
                                        </select>
                                        {selectedUser?.hasActiveSubscription && (
                                            <p className="text-xs text-red-500 mt-1">This user already has an active subscription.</p>
                                        )}
                                        <p className="text-xs text-gray-400 mt-1">Only standalone users are listed - admins and admin-users are excluded.</p>
                                    </div>
                                )}

                                <div>
                                    <label className="block text-sm font-medium mb-1">Plan</label>
                                    <select
                                        value={form.PlanID}
                                        onChange={(e) => setForm({ ...form, PlanID: e.target.value })}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="">Select a plan</option>
                                        {plans.map((p) => (
                                            <option key={p._id} value={p._id}>{p.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Start date</label>
                                        <input
                                            type="date"
                                            value={form.planStartDate}
                                            onChange={(e) => setForm({ ...form, planStartDate: e.target.value })}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Expiry date</label>
                                        <input
                                            type="date"
                                            value={form.planExpiryDate}
                                            onChange={(e) => setForm({ ...form, planExpiryDate: e.target.value })}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Billing cycle</label>
                                        <select
                                            value={form.billingCycle}
                                            onChange={(e) => setForm({ ...form, billingCycle: e.target.value })}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="monthly">Monthly</option>
                                            <option value="quarterly">Quarterly</option>
                                            <option value="half-yearly">Half-yearly</option>
                                            <option value="yearly">Yearly</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Payment status</label>
                                        <select
                                            value={form.paymentStatus}
                                            onChange={(e) => setForm({ ...form, paymentStatus: e.target.value })}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="success">Success</option>
                                            <option value="pending">Pending</option>
                                            <option value="failed">Failed</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </Modal>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ViewSubscriptionsPage
